import React, { useState, useEffect, useRef } from "react";
import { Form, Input, Button, message, Row, Col } from "antd";
import { resetPassword, sendOTPApi } from "../services/auth.service";
import { useNavigate } from "react-router-dom";

const ForgotPassword = () => {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const resendIntervalRef = useRef(null);
  const navigate = useNavigate();

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (resendIntervalRef.current) {
        clearInterval(resendIntervalRef.current);
      }
    };
  }, []);

  const startResendCountdown = () => {
    let countdown = 60;
    setResendDisabled(true);
    setResendTimer(countdown);

    resendIntervalRef.current = setInterval(() => {
      countdown -= 1;
      if (countdown === 0) {
        clearInterval(resendIntervalRef.current);
        setResendDisabled(false);
        setResendTimer(0);
      } else {
        setResendTimer(countdown);
      }
    }, 1000);
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email) {
      message.error("Please enter your email");
      return;
    }
    if (!emailRegex.test(email)) {
      message.error("Please enter a valid email address");
      return;
    }
    if (!newPassword || !confirmPassword) {
      message.error("Please enter password fields!");
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    setLoadingResend(true);

    try {
      const queryParams = new URLSearchParams({ email }).toString();
      const response = await sendOTPApi({ queryParams, email });

      if (response.success) {
        message.success(response.data.message || "OTP sent successfully");
        setResendDisabled(true);
        startResendCountdown();
        setStep(2);
      } else {
        const errorMsg =
          response.error?.response?.data || "Reset failed";
        message.error(errorMsg);
      }
    } catch (error) {
      message.error("Failed to send OTP");
      setResendDisabled(false);
    } finally {
      setLoadingResend(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();

    if (!email || !otp) {
      message.error("Enter your email to receive the OTP and continue.");
      return;
    }

    if (step === 2) {
      if (!otp || !newPassword || !confirmPassword) {
        message.error("All fields are required");
        return;
      }
      if (newPassword !== confirmPassword) {
        message.error("Passwords do not match");
        return;
      }

      setLoading(true);
      try {
        const response = await resetPassword({
          email,
          otp,
          newPassword,
          confirmPassword,
        });

        if (response.success) {
          message.success(
            response.data.message || "Password updated successfully"
          );

          // Reset form
          setStep(1);
          setEmail("");
          setOtp("");
          setNewPassword("");
          setConfirmPassword("");
          setResendDisabled(false);
          setResendTimer(0);

          if (resendIntervalRef.current) {
            clearInterval(resendIntervalRef.current);
          }

          navigate("/signin");
        } else {
          const errorMsg =
            response.error?.response?.data?.message || "Reset failed";
          message.error(errorMsg);
        }
      } catch (error) {
        message.error("Error during password reset");
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <section className="auth-layout-container">
      <div className="gx-app-login-wrap">
        <div className="verify-container">
          <div>
            <p className="authenticator-heading">
              Enter your registered email to receive an OTP
            </p>
          </div>
          <div
            className="verify-container-block forgot-password"
            style={{ marginBottom: "0px" }}
          >
            <div className="gx-app-login-content">
              <Form>
                <Form.Item label="Email">
                  <Input
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="New Password">
                  <Input.Password
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Confirm Password">
                  <Input.Password
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </Form.Item>
                <Form.Item label="Enter OTP">
                  <Row gutter={8} align="middle">
                    <Col span={16} flex="1">
                      <Input
                        style={{ minHeight: "44px" }}
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                      />
                    </Col>
                    <Col span={8}>
                      <Button
                        block
                        type="primary"
                        onClick={handleSendOtp}
                        disabled={resendDisabled}
                        loading={loadingResend}
                      >
                        {resendDisabled
                          ? `Resend OTP (${resendTimer}s)`
                          : "Send OTP"}
                      </Button>
                    </Col>
                  </Row>
                </Form.Item>
              </Form>
            </div>
          </div>
          <Button
            style={{ marginBottom: "0px" }}
            block
            type="primary"
            onClick={handleResetSubmit}
            loading={loading}
          >
            Reset Password
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ForgotPassword;
